import React, {
  useState,
  useEffect,
  useContext,
  ReactNode,
  createContext,
} from 'react'
import Cookies, { CookieAttributes } from 'js-cookie'
import CookieBanner from './components/layout/CookieBanner'
import { loadScript } from './utils/scriptLoader'
import useApi from './templates/useApi'
import { isBrowser } from './react-utils/dom'

const PP_SDK_URL = 'https://www.paypal.com/sdk/js?client-id=sb'

type ProviderProps = {
  children: ReactNode
}

export type PaymentSelection = {
  value: string
  primaryText: string
}

type ApplicationContext = {
  user: any
  setUser: () => void
  isScriptLoaded: boolean
  isPPSDKLoaded: boolean
  setPPSDKLoaded: (value: boolean) => void
  countrySelection: string
  updateCountrySelection: (value: string) => void
  paymentSelection: PaymentSelection['value']
  updatePaymentSelection: (value: PaymentSelection['value']) => void
  elmoData: any
  Cookies: CookieAttributes
  rightNav: any
  setRightNav: any
  experiments: any
}

export const ApplicationContext = createContext({} as ApplicationContext)

export function useApplicationContext() {
  return useContext(ApplicationContext)
}

const Provider = ({ children }: ProviderProps) => {
  const [user, setUser] = useState({})
  const [isScriptLoaded, setScriptLoaded] = useState(false)
  const [isPPSDKLoaded, setPPSDKLoaded] = useState(false)
  const [countrySelection, setCountrySelection] = useState('US')
  const [paymentSelection, setPaymentSelection] =
    useState<PaymentSelection['value']>('paypal')
  const [cookieBanner, setCookieBanner] = useState({})
  const [experiments, setExperiments] = useState({})
  const [rightNav, _setRightNav] = useState()
  const elmoData = useApi('/apis/elmodata')

  // TODO: Type safe for this guy.
  const setRightNav = (nav: any) => {
    _setRightNav(nav)
  }

  // We had been just using setUser to update state whenever we fetched user
  // data from /apis/user, but we'd also like to update FPTI `cust` property
  // in addition to the user data stored in state. This `updateUser` function
  // allows us to both, and it's this function that we pass down to children
  // as setUser via the Provider value
  const updateUser = (data = {}) => {
    // Sets user data in state which can be consumed by children components who
    // wrap themselves with this <Provider />
    setUser(data)

    // If the user is logged in, we will have their encrypted account number
    // TODO: Type safe for this guy.
    // @ts-ignore
    const { encryptedAccountNumber, accountType } = data

    // If we have an encrypted account number, and if pa.js (FPTI) has already
    // been loaded, then we can just set the `cust` so that it will be
    // populated for all subsequent FPTI events
    // @ts-ignore
    if (encryptedAccountNumber && window?.fpti) {
      // @ts-ignore
      window.fpti.cust = encryptedAccountNumber
      window.fpti.acnt = accountType

      // If pa.js/FPTI has not yet loaded, let's set an event listener, which
      // is set up to be fired when pa.js loads (in dx-analytics.js), and then
      // update the cust property then
    } else if (encryptedAccountNumber) {
      window.addEventListener('paLoaded', async () => {
        // @ts-ignore
        window.fpti.cust = encryptedAccountNumber
        window.fpti.acnt = accountType
      })
    }
  }

  const updateCountrySelection = (country: string) => {
    setCountrySelection(country)
  }

  const updatePaymentSelection = (payment: PaymentSelection['value']) => {
    setPaymentSelection(payment)
  }

  useEffect(() => {
    ;(async function () {
      // TODO: we likely don't need to fetch user data here bc we're already
      // doing so in the <Layout /> component, which is rendered on every
      // page load. Also, this useEffect function is likely being called twice,
      // once on initial page load and once when `elmoData` loads/changes
      // @ts-ignore
      if (window && window.app) {
        setScriptLoaded(true)
      } else {
        window.addEventListener('scriptLoad', async () => {
          setScriptLoaded(true)
        })
      }
      try {
        if (!isPPSDKLoaded) {
          await loadScript({ src: PP_SDK_URL })
          setPPSDKLoaded(true)
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('unable to load PayPal SDK')
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elmoData])

  useEffect(() => {
    let cookieBannerInfo =
      isBrowser &&
      //@ts-ignore
      window.__INITIALDATA__ &&
      //@ts-ignore
      window.__INITIALDATA__.cookieBannerInfo
    setCookieBanner(cookieBannerInfo)

    const serverData =
      //@ts-ignore
      (isBrowser && window.__INITIALDATA__) || {}
    setExperiments({
      uiramp: serverData.uiramp,
      signupramp: serverData.signupramp,
      isInternalUser: serverData.isInternalUser,
      integrationBuilder:
        serverData.integrationBuilder === 'Trmt_Developer_Integration_Builder',
    })
  }, [])

  //add elmo data to pass on
  return (
    <ApplicationContext.Provider
      value={{
        user,
        setUser: updateUser,
        isScriptLoaded,
        isPPSDKLoaded,
        setPPSDKLoaded,
        countrySelection,
        updateCountrySelection,
        paymentSelection,
        updatePaymentSelection,
        elmoData,
        Cookies,
        rightNav,
        setRightNav,
        experiments,
      }}
    >
      {children}
      <CookieBanner cookieBannerInfo={cookieBanner} />
    </ApplicationContext.Provider>
  )
}

const ProviderComponent = ({ element }: any) => <Provider>{element}</Provider>

export default ProviderComponent
